<template>
  <div v-if="loader" class="container-fluid message-container-align">
    <b-spinner
      style="width: 6rem; height: 6rem; color: #003778"
      label="Loading"
    ></b-spinner>
  </div>

  <div
    v-else-if="
      invalidIDFlag || unAuthorizedFlag || linkExpiredFlag || errorFlag
    "
    class="container-fluid message-container-align"
  >
    <h2 v-if="unAuthorizedFlag">{{ t("form.unauthorized") }}</h2>
    <h2 v-else-if="linkExpiredFlag">{{ t("form.yourlinkhasexpired") }}</h2>
    <h2 v-else-if="invalidIDFlag">{{ t("form.invalidrequest") }}</h2>
    <h2 v-else>{{ t("form.unabletoprocesstherequest") }}</h2>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import CryptoJS from "crypto-js";
import router from "@/router";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { generateChecksum } from "../services/generateChecksum";
import { decryptData } from "../services/decryptData";

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const loader = ref(false);
const invalidIDFlag = ref(false);
const unAuthorizedFlag = ref(false);
const linkExpiredFlag = ref(false);
const errorFlag = ref(false);

const userDocument = ref<any | null>(null);
const baseUrl = process.env.VUE_APP_BASE_API_URL;
const secretKey = process.env.VUE_APP_ENCRYPT_DECRYPT_SECRET_KEY;
const JwtSecretKey = process.env.VUE_APP_JWT_KEY;
const userId = ref((route.query.p as string) || "");

// token helpers
const setTokenInSessionStorage = (token: any, expiresIn: number) => {
  const expirationTime = Date.now() + expiresIn * 3600 * 1000;
  sessionStorage.setItem(`token_${userId.value}`, token);
  sessionStorage.setItem(
    `tokenExpiration_${userId.value}`,
    expirationTime.toString()
  );
};

const getTokenFromSessionStorage = () => {
  const token = sessionStorage.getItem(`token_${userId.value}`);
  const expirationTime = parseInt(
    sessionStorage.getItem(`tokenExpiration_${userId.value}`) || "0",
    10
  );
  if (!token || Date.now() > expirationTime) {
    sessionStorage.removeItem(`token_${userId.value}`);
    sessionStorage.removeItem(`tokenExpiration_${userId.value}`);
    return null;
  }
  return token;
};

const getToken = async (token: any) => {
  try {
    const encryptedDataValue = {
      UserId: userId.value,
      SecretKey: JwtSecretKey,
      timestamp: new Date().toISOString(),
    };
    const checksumString = Object.values(encryptedDataValue).join("-");
    const checksum = generateChecksum(checksumString);
    const dataString = JSON.stringify(encryptedDataValue);
    const encryptedDataString = CryptoJS.AES.encrypt(
      dataString,
      secretKey
    ).toString();
    const tokenResponse = await axios.post(
      `${baseUrl}/auth/generate-token`,
      { encryptedDataString },
      {
        headers: { "Content-Type": "application/json", "X-Checksum": checksum },
      }
    );
    token = tokenResponse.data.token;
    setTokenInSessionStorage(token, 23);
    return token;
  } catch (error: any) {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 404)
    ) {
      unAuthorizedFlag.value = true;
    } else {
      errorFlag.value = true;
    }
    throw error;
  }
};

/**
 * fetchUserData
 * - fetches invoice metadata
 * - verifies checksum & ownership
 * - checks link expiry (moved here)
 * - if link valid and publicUrl => PDFDownloadPage
 * - else route to OtpValidate or Subscriber based on OtpRequired
 */
const fetchUserData = async () => {
  store.commit("setAuth", false);
  loader.value = true;
  invalidIDFlag.value = false;
  unAuthorizedFlag.value = false;
  linkExpiredFlag.value = false;
  errorFlag.value = false;

  try {
    let token = getTokenFromSessionStorage();
    if (!token) token = await getToken(token);

    const checksumString = `${userId.value}`;
    const checksum = generateChecksum(checksumString);

    const invoiceResponse = await axios.get(
      `${baseUrl}/invoice/${userId.value}`,
      {
        headers: { Authorization: `Bearer ${token}`, "X-Checksum": checksum },
      }
    );

    const encryptedData = invoiceResponse.data;
    const decryptedText = decryptData(encryptedData);
    const receivedChecksum = invoiceResponse.headers["x-file-checksum"];
    const calculatedChecksum = generateChecksum(invoiceResponse.data);
    const invoiceData = JSON.parse(decryptedText);

    if (
      receivedChecksum !== calculatedChecksum ||
      invoiceData.UserId !== userId.value
    ) {
      errorFlag.value = true;
      return;
    }

    userDocument.value = invoiceData;
    //store.commit("setSubscriber", !invoiceData.OtpRequired);
    //store.commit("setDisplayTitle", true);

    // Link expiry logic moved here:
    const currentDate = new Date();
    const expiryRaw =
      userDocument.value?.ExpiryDate?.value ??
      userDocument.value?.ExpiryDate ??
      null;
    const expiryDate = expiryRaw ? new Date(expiryRaw) : null;

    if (expiryDate && currentDate > expiryDate) {
      linkExpiredFlag.value = true;
      return;
    }

    // If document already exposed via publicUrl -> go straight to PDF
    if (!invoiceData.OtpRequired && userDocument.value?.publicUrl) {
      store.commit("setUserID", userDocument.value.Id);
      store.commit("setAuth", true);
      router.push({ name: "PDFDownloadPage", query: { p: userId.value } });
      return;
    }

    // route based on OTP requirement
    if (invoiceData.OtpRequired) {
      store.commit("setOtpValidateAuth", true);
      router.push({ name: "OtpValidate", query: { p: userId.value } });
    } else {
      store.commit("setAuth", true);
      router.push({ name: "Subscriber", query: { p: userId.value } });
    }
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      invalidIDFlag.value = true;
    } else if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      unAuthorizedFlag.value = true;
    } else {
      errorFlag.value = true;
    }
  } finally {
    loader.value = false;
  }
};

onMounted(() => {
  if (!userId.value) {
    invalidIDFlag.value = true;
    return;
  }
  fetchUserData();
});
</script>

<script lang="ts">
export default { name: "HomePage" };
</script>

<style scoped>
.message-container-align {
  min-height: 363px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
